import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

/* ────────────────────────────────────────────────────────────────────────────
   Small feedback primitives: loading, liveness, emptiness, key hints.
   ──────────────────────────────────────────────────────────────────────────── */

export interface SpinnerProps {
  /** Rendered box in px. */
  size?: number;
  className?: string;
}

/** Indeterminate ring. Inherits `currentColor` so it matches its host button. */
export function Spinner({ size = 16, className }: SpinnerProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      aria-hidden="true"
      focusable="false"
      className={cn('shrink-0 animate-spin', className)}
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.25" opacity="0.22" />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        stroke="currentColor"
        strokeWidth="2.25"
        strokeLinecap="round"
      />
    </svg>
  );
}

export interface LiveDotProps {
  tone?: 'up' | 'bull' | 'floor';
  className?: string;
}

const LIVE_TONE: Record<NonNullable<LiveDotProps['tone']>, { core: string; halo: string }> = {
  up: { core: 'bg-up', halo: 'bg-up/60' },
  bull: { core: 'bg-bull', halo: 'bg-bull/60' },
  floor: { core: 'bg-floor', halo: 'bg-floor/60' },
};

/** A 8px "this is streaming" pip: solid core with a slow expanding halo. */
export function LiveDot({ tone = 'up', className }: LiveDotProps) {
  const t = LIVE_TONE[tone];
  return (
    <span className={cn('relative inline-flex h-2 w-2 shrink-0', className)} aria-hidden="true">
      <span className={cn('absolute inset-0 animate-ping rounded-full', t.halo)} />
      <span className={cn('relative inline-flex h-2 w-2 rounded-full', t.core)} />
    </span>
  );
}

export interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}

/** Centred nothing-here panel. Always offers a way forward when it can. */
export function EmptyState({ icon, title, description, action, className }: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-line px-6 py-12 text-center',
        className,
      )}
    >
      {icon ? (
        <span className="grid h-11 w-11 place-items-center rounded-xl border border-line bg-elevated text-subtle">
          {icon}
        </span>
      ) : null}
      <div className="flex flex-col gap-1">
        <p className="font-display text-sm font-semibold text-fg">{title}</p>
        {description ? (
          <p className="mx-auto max-w-sm text-xs leading-relaxed text-muted">{description}</p>
        ) : null}
      </div>
      {action ? <div className="mt-1 flex items-center gap-2">{action}</div> : null}
    </div>
  );
}

export interface KbdProps {
  children: ReactNode;
  className?: string;
}

/** Keycap for shortcut hints. */
export function Kbd({ children, className }: KbdProps) {
  return (
    <kbd
      className={cn(
        'inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded border border-line bg-elevated px-1.5 font-mono text-3xs font-medium text-muted shadow-inner-line',
        className,
      )}
    >
      {children}
    </kbd>
  );
}
